import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { BookOpen, ChevronRight, Lock, Pencil, RefreshCw, Search, Trash2, Upload, X } from "lucide-react";
import type { Tenant } from "@am/shared";
import { fmtDate, RichTextEditor } from "@am/shared";
import { toast } from "sonner";
import { useApiClient } from "../api/client";
import { PageHeader } from "../components/PageHeader";
import { ConfirmModal } from "../components/ConfirmModal";

interface ConfirmConfig {
  title: React.ReactNode;
  body: React.ReactNode;
  okLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
}

interface WcSync {
  status: "running" | "ok" | "failed" | null;
  last_sync_at: string | null;
  result: { products_synced?: number; chunks_upserted?: number; error?: string } | null;
}

interface KbStats {
  total: number;
  embedded: number;
  pending: number;
  outdated: number;
  failed: number;
  total_chunks?: number;
  wc_configured: boolean;
  wc_sync: WcSync;
}

interface KbDoc {
  id: string;
  title: string;
  content: string;
  doc_type: string;
  embed_status: "embedded" | "pending" | "outdated" | "failed";
  embed_error?: string | null;
  chunk_count: number;
  updated_at?: string;
  is_locked?: boolean;
}

const DOC_TYPES = ["faq", "info", "service", "policy", "custom"];

const STATUS_LABEL: Record<KbDoc["embed_status"], string> = {
  embedded: "Indexé",
  pending: "En attente",
  outdated: "Obsolète",
  failed: "Échoué",
};

export function KbPage() {
  const api = useApiClient();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [picker, setPicker] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState<Tenant | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [stats, setStats] = useState<KbStats | null>(null);
  const [docs, setDocs] = useState<KbDoc[]>([]);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<KbDoc | "new" | null>(null);
  const [confirm, setConfirm] = useState<ConfirmConfig | null>(null);

  // Load tenant list once.
  useEffect(() => {
    (async () => {
      try {
        const list = await api<Tenant[]>("/admin/api/tenants?include_inactive=false");
        setTenants(list ?? []);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Erreur de chargement");
      }
    })();
  }, [api, toast]);

  const loadKb = useCallback(
    async (tenantId: string) => {
      try {
        const [statsRes, docsRes] = await Promise.all([
          api<KbStats>(`/admin/api/tenants/${tenantId}/kb`),
          api<{ docs: KbDoc[] }>(`/admin/api/tenants/${tenantId}/kb/docs?limit=200`),
        ]);
        setStats(statsRes);
        setDocs(docsRes?.docs ?? []);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Erreur de chargement");
      }
    },
    [api, toast],
  );

  useEffect(() => {
    if (selected) void loadKb(selected.id);
  }, [selected, loadKb]);

  const pickerFiltered = useMemo(() => {
    const q = picker.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) => t.name.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q));
  }, [tenants, picker]);

  const docsFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (filterType && d.doc_type !== filterType) return false;
      if (q && !d.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [docs, filterType, search]);

  async function handleEdit(doc: KbDoc) {
    if (!selected) return;
    try {
      const full = await api<KbDoc>(`/admin/api/tenants/${selected.id}/kb/docs/${doc.id}`);
      setEditing(full);
    } catch {
      toast.error("Impossible de charger le document");
    }
  }

  function onDelete(doc: KbDoc) {
    if (!selected) return;
    setConfirm({
      title: <>Supprimer le <em>document</em></>,
      body: (
        <>
          Supprimer <strong>« {doc.title} »</strong> ? Cette action supprimera aussi les vecteurs Qdrant associés.
        </>
      ),
      okLabel: "Supprimer",
      danger: true,
      onConfirm: async () => {
        await api(`/admin/api/tenants/${selected.id}/kb/docs/${doc.id}`, { method: "DELETE" });
        setConfirm(null);
        toast.success("Document supprimé.");
        await loadKb(selected.id);
      },
    });
  }

  async function embedAll() {
    if (!selected) return;
    try {
      const res = await api<{ embedded?: number; failed?: number }>(
        `/admin/api/tenants/${selected.id}/kb/embed-all`,
        { method: "POST" },
      );
      toast.success(`Synchronisation : ${res?.embedded ?? 0} ok, ${res?.failed ?? 0} échec(s).`);
      await loadKb(selected.id);
    } catch (err) {
      toast.error("Erreur : " + (err instanceof Error ? err.message : "inconnue"));
    }
  }

  const [wcSyncing, setWcSyncing] = useState(false);

  async function wcSync() {
    if (!selected) return;
    setWcSyncing(true);
    try {
      await api(`/admin/api/tenants/${selected.id}/kb/wc-sync`, { method: "POST" });
      // Poll every 3s until status leaves "running"
      pollRef.current = setInterval(async () => {
        try {
          const s = await api<KbStats>(`/admin/api/tenants/${selected.id}/kb`);
          setStats(s);
          if (s?.wc_sync?.status !== "running") {
            clearInterval(pollRef.current!);
            pollRef.current = null;
            setWcSyncing(false);
          }
        } catch {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setWcSyncing(false);
        }
      }, 3000);
    } catch (err) {
      toast.error("Erreur WooCommerce : " + (err instanceof Error ? err.message : "vérifiez les credentials"));
      setWcSyncing(false);
    }
  }

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  return (
    <div className="page is-active">
      <PageHeader
        layout="split"
        title={<>Base de <em>connaissances</em></>}
        subtitle="Gérez les documents de n'importe quel tenant"
      >
        {selected && (
          <button
            type="button"
            className="btn-sm btn-primary"
            style={{ alignSelf: "center" }}
            onClick={() => setEditing("new")}
          >
            + Nouveau document
          </button>
        )}
      </PageHeader>

      {/* Tenant picker */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <div className="kb-tenant-picker">
          <Search className="kb-tenant-picker-icon" size={15} />
          <input
            type="text"
            className="kb-tenant-picker-input"
            placeholder="Rechercher un tenant…"
            autoComplete="off"
            value={picker}
            onChange={(e) => setPicker(e.target.value)}
            onFocus={() => setPickerOpen(true)}
            onBlur={() => window.setTimeout(() => setPickerOpen(false), 150)}
          />
          {selected && (
            <button
              type="button"
              className="kb-tenant-picker-clear"
              onClick={() => {
                setSelected(null);
                setPicker("");
                setStats(null);
                setDocs([]);
              }}
              title="Effacer"
            >
              <X size={14} />
            </button>
          )}
          {pickerOpen && (
            <div className="kb-tenant-dropdown is-open">
              {pickerFiltered.length === 0 ? (
                <div className="kb-tenant-option" style={{ color: "var(--text-muted)" }}>
                  Aucun tenant
                </div>
              ) : (
                pickerFiltered.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="kb-tenant-option"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSelected(t);
                      setPicker(t.name);
                      setPickerOpen(false);
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{t.name}</div>
                    <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {t.slug}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        {selected && (
          <button type="button" className="btn-sm btn-ghost" onClick={() => loadKb(selected.id)} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={12} /> Actualiser
          </button>
        )}
      </div>

      {!selected && (
        <div>
          {tenants.length > 0 ? (
            <div className="kb-tenant-list">
              {tenants.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className="kb-tenant-card"
                  onClick={() => {
                    setSelected(t);
                    setPicker(t.name);
                  }}
                >
                  <div className="kb-tenant-card-avatar">
                    {t.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="kb-tenant-card-info">
                    <div className="kb-tenant-card-name">{t.name}</div>
                    <div className="kb-tenant-card-slug">{t.slug}</div>
                  </div>
                  <ChevronRight className="kb-tenant-card-arrow" size={18} />
                </button>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "80px 24px" }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16,
                background: "var(--bg-elevated)", border: "1px solid var(--border)",
                display: "grid", placeItems: "center", margin: "0 auto 20px",
              }}>
                <BookOpen size={28} style={{ color: "var(--text-muted)" }} />
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 400, marginBottom: 8 }}>
                Aucun tenant
              </div>
              <div style={{ color: "var(--text-muted)", fontSize: 14 }}>
                Créez un tenant pour commencer.
              </div>
            </div>
          )}
        </div>
      )}

      {selected && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns: "repeat(5,1fr)", marginBottom: 16 }}>
            <Stat label="Documents" value={stats?.total ?? "—"} valueClass="purple" />
            <Stat label="Indexés" value={stats?.embedded ?? "—"} valueColor="var(--green)" />
            <Stat
              label="En attente"
              value={stats ? (stats.pending ?? 0) + (stats.outdated ?? 0) : "—"}
              valueColor="var(--yellow)"
            />
            <Stat label="Échoués" value={stats?.failed ?? "—"} valueColor="var(--red)" />
            <Stat label="Chunks Qdrant" value={stats?.total_chunks ?? "—"} />
          </div>

          {stats?.wc_configured && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                background: "var(--bg-card)",
                border: "1px solid var(--border)",
                borderRadius: 12,
                padding: "14px 18px",
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 3 }}>WooCommerce</div>
                <WcSyncStatus sync={stats.wc_sync} />
              </div>
              <button
                type="button"
                className="btn-sm btn-ghost"
                onClick={wcSync}
                disabled={wcSyncing}
                style={{ whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}
              >
                <RefreshCw size={12} className={wcSyncing ? "spin" : ""} />
                {wcSyncing ? "Synchronisation…" : "Synchroniser WC"}
              </button>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <FilterBtn active={filterType === null} onClick={() => setFilterType(null)} label="Tous" />
              {DOC_TYPES.map((dt) => (
                <FilterBtn
                  key={dt}
                  active={filterType === dt}
                  onClick={() => setFilterType(dt)}
                  label={dt === "policy" ? "Politique" : dt.charAt(0).toUpperCase() + dt.slice(1)}
                />
              ))}
            </div>
            <input
              type="search"
              placeholder="Rechercher un document…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: 1,
                minWidth: 160,
                maxWidth: 240,
                background: "var(--bg-elevated)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: "7px 12px",
                fontSize: 13,
                color: "var(--text)",
                fontFamily: "var(--font-body)",
              }}
            />
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button type="button" className="btn-sm btn-ghost" onClick={embedAll} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Upload size={12} /> Synchroniser tout
              </button>
            </div>
          </div>

          <div className="card">
            <table>
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th>Chunks</th>
                  <th>Mis à jour</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {docsFiltered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty">
                      Aucun document
                    </td>
                  </tr>
                ) : (
                  docsFiltered.map((doc) => (
                    <tr key={doc.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{doc.title}</div>
                        {doc.is_locked && (
                          <span className="badge badge-purple" style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Lock size={10} /> Protégé
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`kb-doc-type-badge type-${doc.doc_type}`}>{doc.doc_type}</span>
                      </td>
                      <td>
                        <span className={`kb-doc-status st-${doc.embed_status}`}>{STATUS_LABEL[doc.embed_status]}</span>
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: 11 }}>
                          {doc.chunk_count}
                        </span>
                      </td>
                      <td>
                        <span className="mono" style={{ fontSize: 11 }}>
                          {fmtDate(doc.updated_at)}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <button
                            type="button"
                            className="kb-icon-btn"
                            title="Modifier"
                            onClick={() => void handleEdit(doc)}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            className="kb-icon-btn is-danger"
                            title="Supprimer"
                            onClick={() => onDelete(doc)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing && selected && (
        <KbDocModal
          tenantId={selected.id}
          doc={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await loadKb(selected.id);
          }}
        />
      )}

      <ConfirmModal
        open={confirm !== null}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.okLabel}
        danger={confirm?.danger}
        onConfirm={async () => {
          if (confirm) await confirm.onConfirm();
        }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function WcSyncStatus({ sync }: { sync: WcSync | undefined }) {
  if (!sync?.status) return <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Aucune synchronisation effectuée.</div>;
  if (sync.status === "running") return <div style={{ fontSize: 12, color: "var(--yellow)" }}>Synchronisation en cours…</div>;
  if (sync.status === "failed") return (
    <div style={{ fontSize: 12, color: "var(--red)" }}>
      Échec — {sync.result?.error ?? "erreur inconnue"}
      {sync.last_sync_at && <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>{sync.last_sync_at.slice(0, 16).replace("T", " ")}</span>}
    </div>
  );
  return (
    <div style={{ fontSize: 12, color: "var(--green)" }}>
      {sync.result?.products_synced ?? 0} produits · {sync.result?.chunks_upserted ?? 0} chunks
      {sync.last_sync_at && <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>{sync.last_sync_at.slice(0, 16).replace("T", " ")}</span>}
    </div>
  );
}

function Stat({
  label,
  value,
  valueClass,
  valueColor,
}: {
  label: string;
  value: number | string;
  valueClass?: string;
  valueColor?: string;
}) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className={"stat-value" + (valueClass ? " " + valueClass : "")} style={valueColor ? { color: valueColor } : undefined}>
        {value}
      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" className={"kb-filter-btn" + (active ? " is-active" : "")} onClick={onClick}>
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------

interface ModalProps {
  tenantId: string;
  doc: KbDoc | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function KbDocModal({ tenantId, doc, onClose, onSaved }: ModalProps) {
  const api = useApiClient();
  const [title, setTitle] = useState(doc?.title ?? "");
  const [content, setContent] = useState(doc?.content ?? "");
  const [type, setType] = useState(doc?.doc_type ?? "faq");
  const [locked, setLocked] = useState(doc?.is_locked ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    const c = content.trim();
    if (!t) {
      setError("Le titre est obligatoire.");
      return;
    }
    if (!c) {
      setError("Le contenu est obligatoire.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      if (doc) {
        await api(`/admin/api/tenants/${tenantId}/kb/docs/${doc.id}`, {
          method: "PUT",
          body: { title: t, content: c, doc_type: type, is_locked: locked },
        });
      } else {
        await api(`/admin/api/tenants/${tenantId}/kb/docs`, {
          method: "POST",
          body: { title: t, content: c, doc_type: type, is_locked: locked },
        });
      }
      toast.success(doc ? "Document mis à jour !" : "Document créé !");
      await onSaved();
    } catch (err) {
      setError("Erreur : " + (err instanceof Error ? err.message : "impossible d'enregistrer"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="kb-modal-overlay is-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="kb-modal">
        <div className="kb-modal-head">
          <div className="kb-modal-title">
            {doc ? (
              <>Modifier le <em>document</em></>
            ) : (
              <>Nouveau <em>document</em></>
            )}
          </div>
          <button type="button" className="kb-modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="kb-modal-body">
            <div className="field">
              <label>Titre</label>
              <input
                type="text"
                placeholder="Ex: FAQ Livraison, Politique de retour…"
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
              />
            </div>
            <div className="field">
              <label>Type de document</label>
              <div className="kb-type-tabs">
                {DOC_TYPES.map((dt) => (
                  <button
                    key={dt}
                    type="button"
                    className={"kb-type-tab" + (type === dt ? " is-active" : "")}
                    onClick={() => setType(dt)}
                  >
                    {dt === "policy" ? "Politique" : dt.charAt(0).toUpperCase() + dt.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label>Contenu (Markdown)</label>
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Rédigez le contenu : titre, listes, tableau, lien…"
                minHeight={320}
              />
              <div className="kb-char-count">{content.length} caractères</div>
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "11px 14px",
                borderRadius: 8,
                background: "var(--bg-elevated)",
                border: "1px solid var(--border)",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <input
                type="checkbox"
                checked={locked}
                onChange={(e) => setLocked(e.target.checked)}
                style={{ width: 15, height: 15, accentColor: "var(--purple)", cursor: "pointer", flexShrink: 0 }}
              />
              <Lock size={15} style={{ color: "var(--purple)", flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Verrouiller ce document</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
                  Invisible et non modifiable par l'admin du tenant
                </div>
              </div>
            </label>
            {error && (
              <div style={{ fontSize: 12, color: "var(--red)", padding: "8px 0" }}>{error}</div>
            )}
          </div>
          <div className="kb-modal-foot">
            <button type="button" className="btn-sm btn-ghost" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-sm btn-primary" disabled={saving}>
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
